import React, { useState, useRef, useEffect } from "react";
import { Badge } from "../ui/Badge";
import { PlayerDragCard } from "./PlayerDragCard";
import { PencilIcon } from "@heroicons/react/24/outline";

const TeamColumn = ({
  team,
  teamIndex,
  stats,
  selectedPlayerId,
  onPlayerClick,
  onDropPlayer,
  onRenameTeam,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(team.name);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditName(team.name);
  }, [team.name]);

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const trimmedName = editName.trim();
    if (trimmedName && trimmedName !== team.name) {
      onRenameTeam(trimmedName);
    } else {
      setEditName(team.name);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveEdit();
    } else if (e.key === "Escape") {
      setEditName(team.name);
      setIsEditing(false);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("ring-2", "ring-slate-400");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("ring-2", "ring-slate-400");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("ring-2", "ring-slate-400");

    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      if (data.playerId && data.fromTeamIndex !== undefined) {
        onDropPlayer(data.playerId, data.fromTeamIndex);
      }
    } catch (error) {
      console.error("Drop error:", error);
    }
  };

  const handleColumnClick = () => {
    // If a player is selected, clicking the column moves them here
    if (selectedPlayerId) {
      // The parent handles finding the source team
      onPlayerClick({ id: selectedPlayerId }, teamIndex);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4) return "text-green-600";
    if (score >= 3) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div
      className={`bg-white border rounded-lg shadow-sm overflow-hidden transition-all ${
        selectedPlayerId ? "cursor-pointer hover:border-slate-400" : ""
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleColumnClick}
    >
      {/* Header */}
      <div className="bg-gray-100 px-3 py-2 border-b">
        <div className="flex items-center justify-between">
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              className="font-medium text-gray-900 bg-white border border-slate-400 rounded px-1 py-0.5 text-sm w-32 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          ) : (
            <button
              onClick={handleStartEdit}
              className="group flex items-center gap-1 font-medium text-gray-900 hover:text-slate-600"
            >
              <span>{team.name}</span>
              <PencilIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 text-gray-400" />
            </button>
          )}
          <Badge variant="gray" className="text-xs">
            {stats.count} players
          </Badge>
        </div>
        {stats.count > 0 && (
          <div className="flex items-center gap-2 mt-1 text-xs">
            <span className="text-gray-500">Avg:</span>
            <span
              className={`font-medium ${getScoreColor(stats.averageScore)}`}
            >
              {stats.averageScore.toFixed(2)}
            </span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-500">
              {stats.minScore.toFixed(1)} - {stats.maxScore.toFixed(1)}
            </span>
          </div>
        )}
      </div>

      {/* Players */}
      <div className="p-2 min-h-[200px] max-h-[400px] overflow-y-auto">
        {team.players.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
            No players
          </div>
        ) : (
          <div className="space-y-1">
            {team.players.map((player) => (
              <PlayerDragCard
                key={player.id}
                player={player}
                teamIndex={teamIndex}
                isSelected={selectedPlayerId === player.id}
                onClick={() => onPlayerClick(player, teamIndex)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { TeamColumn };
